import {
  OnboardPatientContext,
  OnboardPractitionerContext,
  OnboardStudentContext,
} from '@dfh-corp-sites/onboarding/context'
import { catchError, filter, map, Observable, startWith, switchMap, take } from 'rxjs'
import { fromFetch } from 'rxjs/fetch'

import { isNotNull } from '../../../utils/collectionTools'
import { CustomError, FetchError, logAndCaptureException } from '../../../utils/errorTools'
import { LeftResult, Result, right, RightResult } from '../../../utils/result'
import { coerceCaughtToLeftWith } from '../../../utils/rx/errors'
import { retryWithBackoffWhen, tapLeft } from '../../../utils/rx/operators'
import { latestAccessToken$ } from '../../auth/state'
import {
  fetchResponseToJsonResult,
  fetchResponseToVoidResult,
  headers,
  unwrapFetchResult,
} from '../../fetch'

export interface OnboardPersistState {
  state: string
  context?: OnboardPractitionerContext | OnboardPatientContext | OnboardStudentContext
}

export interface OnboardPersistOptions {
  token: string
}

export class OnboardUpdateError extends CustomError {
  override name = 'OnboardUpdateError'
}

export class OnboardDeleteError extends CustomError {
  override name = 'OnboardDeleteError'
}

export const persistence = {
  practitionerApiUrl: '/api/onboarding/practitioner',
  patientApiUrl: '/api/onboarding/patient',
  studentApiUrl: '/api/onboarding/student',
} as const

type PersistenceApiUrl = (typeof persistence)[keyof typeof persistence]

const updateState = (
  apiUrl: PersistenceApiUrl,
  stateToPersist: OnboardPersistState,
  token: string,
): Observable<Result<OnboardPersistState, OnboardUpdateError>> =>
  fromFetch(apiUrl, {
    body: JSON.stringify({ data: stateToPersist }),
    // NOTE: this should probably be a PUT instead
    method: 'POST',
    headers: {
      ...headers.acceptJson,
      ...headers.authorization(token),
      ...headers.contentTypeJson,
    },
  }).pipe(
    switchMap((res) => fetchResponseToJsonResult<OnboardPersistState>(res)),
    map(unwrapFetchResult),
    retryWithBackoffWhen((err) =>
      // retry network and server errors
      err instanceof FetchError ? !!err.response && err.response.status >= 500 : true,
    ),
    map(right),
    catchError(
      coerceCaughtToLeftWith(
        (error) =>
          new OnboardUpdateError(
            `Failed to update onboard state (url: ${apiUrl}): ${error.message}`,
            error,
          ),
      ),
    ),
  )

export const deleteState =
  (apiUrl: PersistenceApiUrl) =>
  ({ token }: { token: string }): Observable<Result<void, OnboardDeleteError>> =>
    fromFetch(apiUrl, {
      method: 'DELETE',
      headers: {
        ...headers.authorization(token),
      },
    }).pipe(
      switchMap(fetchResponseToVoidResult),
      map(unwrapFetchResult),
      retryWithBackoffWhen((err) =>
        // retry network and server errors
        err instanceof FetchError ? !!err.response && err.response.status >= 500 : true,
      ),
      map(right),
      catchError(
        coerceCaughtToLeftWith(
          (error) =>
            new OnboardDeleteError(
              `Failed to delete onboard state (url: ${apiUrl}): ${error.message}`,
              error,
            ),
        ),
      ),
      tapLeft(({ error }) => {
        logAndCaptureException(error)
      }),
    )

export type PersistStateFactoryResult = {
  loading: boolean
  called: boolean
  result: LeftResult<OnboardUpdateError> | RightResult<OnboardPersistState | undefined> | null
}

export const persistStateFactory =
  (apiUrl: PersistenceApiUrl) =>
  (stateToPersist: OnboardPersistState): Observable<PersistStateFactoryResult> =>
    latestAccessToken$.pipe(
      filter(isNotNull),
      take(1),
      switchMap((token) =>
        updateState(apiUrl, stateToPersist, token).pipe(
          tapLeft(({ error }) => {
            logAndCaptureException(error)
          }),
          map((result) => ({ called: true, loading: false, result })),
          startWith({ called: true, loading: true, result: null }),
        ),
      ),
      startWith({ called: false, loading: false, result: null }),
    )
